import * as React from 'react'

import InputSuggestions from 'components/InputSuggestions'
import { TextInputFieldV2 as TextInputField } from './'

import { useCountry, DEFAULT_COUNTRY } from 'lib/withCountry'
import useDebouncedValue from 'lib/useDebouncedValue'
import { getSuggestions } from 'utils/places'
import AuthContext from 'lib/authContext'

import { FormikProps } from 'formik'

type Props = {
  field: {
    name: string
    value: string | null
  }
  form: FormikProps<any>
  label: string
  forceFloat: boolean
  additionalAction?: React.ReactNode
}

export default function PlacesTextInputField(props: Props) {
  const { field, form } = props
  const { value, name } = field
  const authContext = React.useContext(AuthContext)

  const fieldWithHandlers = {
    ...field,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(name, e.target.value)
    },
    onBlur: () => {
      form.setFieldTouched(name, true)
    },
  }

  const meta = {
    value: field.value,
    error: form.errors[name] as string,
    touched: form.touched[name] as boolean,
    initialValue: form.initialValues[name],
    initialTouched: false,
    initialError: undefined,
  }

  const { country } = useCountry()
  const countryCode = (country || DEFAULT_COUNTRY).toUpperCase()
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Generate session token based on user ID and current date
  const sessionToken = React.useMemo(() => {
    const userId = authContext.userData?.id || 'anonymous'
    const dateStr = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const tokenString = `${userId}_${dateStr}`

    // Create a hash of the entire string
    let hash = 0
    for (let i = 0; i < tokenString.length; i++) {
      const char = tokenString.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36)
  }, [authContext.userData?.id])

  const [suggestions, setSuggestions] = React.useState<Array<{ value: string; label: string }> | null>(null)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = React.useState<
    number | null
  >(null)

  const debouncedValue = useDebouncedValue(value)
  const debouncedValueRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    async function fetchSuggestions(address: string) {
      let nextSuggestions

      try {
        nextSuggestions = await getSuggestions(address, sessionToken, {
          componentRestrictions: { country: countryCode },
        })
      } catch (error) {
        console.log(error)
      }

      const currentInputRef = inputRef.current
      const isFocused =
        Boolean(currentInputRef) && currentInputRef === document.activeElement

      if (nextSuggestions && isFocused) {
        setSelectedSuggestionIndex(null)
        setSuggestions(
          nextSuggestions
            .map(({ id: value, displayText: label }) => ({
              value,
              label,
            }))
            .filter(({ label }) => label),
        )
      }
    }

    if (debouncedValueRef.current !== debouncedValue) {
      if (debouncedValue) {
        fetchSuggestions(debouncedValue)
      } else {
        setSelectedSuggestionIndex(null)
        setSuggestions(null)
      }
    }

    debouncedValueRef.current = debouncedValue
  }, [debouncedValue, country, sessionToken, countryCode])

  function handleInputBlur() {
    setSelectedSuggestionIndex(null)
    setSuggestions(null)
  }

  const handleInputKeyDown = React.useCallback(
    function (event: KeyboardEvent) {
      const { keyCode } = event

      if (keyCode === 40) {
        // Key down
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          if (suggestions.length - 1 !== selectedSuggestionIndex) {
            event.preventDefault()
            setSelectedSuggestionIndex(
              typeof selectedSuggestionIndex === 'number'
                ? selectedSuggestionIndex + 1
                : 0,
            )
          }
        }
      } else if (keyCode === 38) {
        // Key up
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          if (
            typeof selectedSuggestionIndex === 'number' &&
            selectedSuggestionIndex !== 0
          ) {
            event.preventDefault()
            setSelectedSuggestionIndex(selectedSuggestionIndex - 1)
          }
        }
      } else if (keyCode === 13) {
        // Enter key
        if (
          typeof selectedSuggestionIndex === 'number' &&
          Array.isArray(suggestions) &&
          suggestions.length > 0
        ) {
          const selectedSuggestion = suggestions[selectedSuggestionIndex]

          const currentInputRef = inputRef.current
          if (currentInputRef) {
            event.preventDefault()

            currentInputRef.blur()
            form.setFieldValue(field.name, selectedSuggestion.label)
          }
        }
      }
    },
    [suggestions, selectedSuggestionIndex, field.name, form],
  )

  function handleSuggestionSelect(suggestion: { value: string; label: string }) {
    const currentInputRef = inputRef.current
    if (currentInputRef) {
      currentInputRef.blur()
      form.setFieldValue(field.name, suggestion.label)
    }
  }

  React.useEffect(() => {
    const currentInputRef = inputRef.current
    if (currentInputRef) {
      currentInputRef.addEventListener('blur', handleInputBlur)
      currentInputRef.addEventListener('keydown', handleInputKeyDown)

      return function cleanup() {
        currentInputRef.removeEventListener('blur', handleInputBlur)
        currentInputRef.removeEventListener('keydown', handleInputKeyDown)
      }
    }
  }, [suggestions, selectedSuggestionIndex, handleInputKeyDown])

  return (
    <div className="root">
      <TextInputField
        field={fieldWithHandlers}
        form={form}
        meta={meta}
        ref={inputRef}
        label={props.label}
        forceFloat={props.forceFloat}
      />

      {suggestions ? (
        <InputSuggestions
          suggestions={suggestions}
          selectedIndex={selectedSuggestionIndex}
          onSelect={handleSuggestionSelect}
          additionalAction={props.additionalAction}
        />
      ) : null}
      <style jsx>{`
        .root {
          position: relative;
        }
      `}</style>
    </div>
  )
}
